import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import { getAdminKycDocumentUrl, type KycDocType } from '@shared/api/admin';
import { useAdminKycOne, useApproveKyc, useRejectKyc } from '@/hooks/use-admin-kyc';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

const PROFESSION_LABELS: Record<string, string> = {
  LAWYER: 'Lawyer',
  CHARTERED_ACCOUNTANT: 'Chartered Accountant',
  COMPANY_SECRETARY: 'Company Secretary',
  COST_MANAGEMENT_ACCOUNTANT: 'Cost & Management Accountant',
  DOCUMENT_WRITER: 'Document Writer',
  TAX_CONSULTANT: 'Tax Consultant',
  OTHER_DEGREE_HOLDER: 'Other / Degree Holder',
};

export function KycReviewDialog({
  kycId,
  open,
  onClose,
}: {
  kycId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: kyc, isLoading } = useAdminKycOne(kycId ?? undefined);
  const { canEdit, canDelete, isSuperAdmin } = useSectionPermission('KYC');
  const approve = useApproveKyc();
  const reject = useRejectKyc();
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');

  function handleApprove() {
    if (!kycId) return;
    approve.mutate(kycId, { onSuccess: () => onClose() });
  }

  function handleReject() {
    if (!kycId || reason.trim().length < 5) return;
    reject.mutate(
      { id: kycId, reason: reason.trim() },
      {
        onSuccess: () => {
          setReason('');
          setShowReject(false);
          onClose();
        },
      },
    );
  }

  const status = kyc?.user.associateProfile?.kycStatus;
  const canAct = status === 'PENDING' && (canEdit || canDelete || isSuperAdmin);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (approve.isPending || reject.isPending) return;
        setReason('');
        setShowReject(false);
        onClose();
      }}
      title={
        kyc
          ? `${kyc.user.firstName ?? ''} ${kyc.user.lastName ?? ''}`.trim() || kyc.user.email
          : 'KYC submission'
      }
      className="max-w-2xl"
      footer={
        canAct ? (
          showReject ? (
            <>
              <Button variant="ghost" onClick={() => setShowReject(false)}>
                Back
              </Button>
              <Button
                onClick={handleReject}
                disabled={reason.trim().length < 5 || reject.isPending}
              >
                {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} className="mr-auto">
                Back
              </Button>
              {(canDelete || isSuperAdmin) && (
                <Button variant="outline" onClick={() => setShowReject(true)}>
                  Reject
                </Button>
              )}
              {(canEdit || isSuperAdmin) && (
                <Button onClick={handleApprove} disabled={approve.isPending}>
                  {approve.isPending ? 'Approving…' : 'Approve'}
                </Button>
              )}
            </>
          )
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {isLoading || !kyc ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : showReject ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Provide a reason — it&rsquo;s shown back to the Associate on their
            KYC page so they know what to fix on resubmission.
          </p>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Aadhaar scan is blurry; please re-upload"
          />
          <p className="text-xs text-gray-500">Min 5 characters.</p>
          {reject.isError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(reject.error)}</p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-5 text-sm">
          <Section title="Associate">
            <Row label="Email" value={kyc.user.email} />
            <Row
              label="Name"
              value={`${kyc.user.firstName ?? ''} ${kyc.user.lastName ?? ''}`.trim() || '—'}
            />
            <Row label="Mobile" value={kyc.user.mobile ?? '—'} />
            <Row
              label="Profession"
              value={
                kyc.user.associateProfile
                  ? PROFESSION_LABELS[kyc.user.associateProfile.profession] ??
                    kyc.user.associateProfile.profession
                  : '—'
              }
            />
            <Row
              label="Account"
              value={
                kyc.user.associateProfile?.isFranchise
                  ? `Franchise — ${kyc.user.associateProfile.outletName ?? ''}`
                  : 'Individual Associate'
              }
            />
            <Row label="Status" value={<span className="font-semibold">{status ?? 'UNKNOWN'}</span>} />
          </Section>

          <Section title="Identity">
            <Row label="Aadhaar (last 4)" value={kyc.aadhaarLast4 ?? '—'} />
            <Row label="PAN" value={kyc.panNumber ?? '—'} />
            <Row label="GSTIN" value={kyc.gstin ?? '—'} />
            <Row label="Address" value={kyc.address ?? '—'} />
          </Section>

          <Section title="Bank">
            <Row label="Account (last 4)" value={kyc.bankAccountLast4 ?? '—'} />
            <Row label="Bank" value={kyc.bankName ?? '—'} />
            <Row label="Branch" value={kyc.bankBranch ?? '—'} />
            <Row label="IFSC" value={kyc.ifscCode ?? '—'} />
          </Section>

          <Section title="Documents">
            <Row
              label="Aadhaar"
              value={
                kyc.aadhaarDocS3Key ? (
                  <DocViewButton kycId={kyc.id} type="AADHAAR" label="Open Aadhaar scan" />
                ) : (
                  <span className="text-gray-500">— not uploaded</span>
                )
              }
            />
            <Row
              label="PAN"
              value={
                kyc.panDocS3Key ? (
                  <DocViewButton kycId={kyc.id} type="PAN" label="Open PAN scan" />
                ) : (
                  <span className="text-gray-500">— not uploaded</span>
                )
              }
            />
            <p className="mt-2 text-[11px] text-gray-500">
              Each click mints a 5-minute presigned URL and is recorded in the
              audit log. Open in a new tab to inspect — the link expires
              quickly, refetch if it stops loading.
            </p>
          </Section>

          {kyc.rejectionReason ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <span className="font-medium">Previous rejection:</span> {kyc.rejectionReason}
            </div>
          ) : null}

          {approve.isError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(approve.error)}</p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h3>
      <dl className="mt-2 grid grid-cols-[150px_1fr] gap-x-3 gap-y-1">{children}</dl>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </>
  );
}

function DocViewButton({
  kycId,
  type,
  label,
}: {
  kycId: string;
  type: KycDocType;
  label: string;
}) {
  // Fetch the URL on click rather than on dialog open so we don't mint
  // (and audit-log) URLs the reviewer never opens. window.open is invoked
  // synchronously from the click handler so popup-blockers treat it as a
  // user gesture even though the URL resolves asynchronously.
  const fetchUrl = useMutation({
    mutationFn: () => getAdminKycDocumentUrl(kycId, type),
  });

  function handleClick() {
    // Open the placeholder WITHOUT noopener — passing noopener nulls the
    // returned WindowProxy and we'd lose the handle we need to redirect.
    // We sever opener manually after assigning the URL so the S3 page
    // can't reach back into the admin window.
    const tab = window.open('about:blank', '_blank');
    fetchUrl.mutate(undefined, {
      onSuccess: ({ url }) => {
        if (tab) {
          tab.opener = null;
          tab.location.replace(url);
        } else {
          // Popup blocked — fall back to a same-tab nav of a new window.
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
      onError: () => {
        if (tab) tab.close();
      },
    });
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={fetchUrl.isPending}
        className="inline-flex w-fit items-center gap-1 rounded-md border border-brand-300 bg-brand-50 px-2 py-1 text-xs font-medium text-brand-800 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {fetchUrl.isPending ? 'Opening…' : label}
        <span aria-hidden>↗</span>
      </button>
      {fetchUrl.isError ? (
        <p className="text-[11px] text-red-600">{apiErrorMessage(fetchUrl.error)}</p>
      ) : null}
    </div>
  );
}
